/****************
 * PAGING THAT CANNOT POINT PAST THE END.
 *
 * Guillaume, 2026-09-23, reporting the dashboard's Include Heavy Losers switch
 * as broken: "I filter trader by PnL to keep only traders who lost more than
 * 1k it doesn't show anybody. Must be a row cap issue again."
 *
 * It was not a row cap and the switch was working. The API returned 217
 * traders with it on and 187 without, 28 correctly flagged below the floor.
 * The table was blank because the page INDEX outlived the list: ten to a page,
 * 217 traders is 22 pages, filtering to "lost more than $1,000" leaves 87
 * which is 9 -- and nothing moved the index, so slice() ran off the end.
 *
 * Clicking a column header reset the page. Typing a filter did not, and
 * neither did the favourites toggle, the floor switch or the period. That
 * asymmetry is why sorting felt fine and filtering looked broken.
 *
 * ── Why this is a function and not another reset ────────────────────────────
 *
 * A reset is a list of things somebody has to remember. Every control added to
 * that table from now on would be one more chance to forget, and the symptom
 * is an empty table that reads as "no results" rather than as a bug -- which
 * is exactly how this survived long enough to be reported as a row cap.
 *
 * Clamping makes the invalid state unrepresentable instead. It also covers the
 * case no reset can: these lists refetch on a poll, so they can shrink while
 * the user is touching nothing at all.
 ****************/

/**
 * clampPage returns a page index that is guaranteed to exist for a list of
 * `total` rows at `perPage` to a page. Never negative: a negative index makes
 * slice() count from the END, which would quietly show the last rows of the
 * list instead of the first -- a wrong answer rather than an empty one.
 */
export function clampPage(page: number, total: number, perPage: number): number {
  const lastPage = Math.max(0, Math.ceil(total / perPage) - 1);
  if (!Number.isFinite(page) || page < 0) return 0;
  return Math.min(page, lastPage);
}

/** pageSlice takes the rows for a page, clamping the index first. */
export function pageSlice<T>(rows: T[], page: number, perPage: number): T[] {
  const safe = clampPage(page, rows.length, perPage);
  return rows.slice(safe * perPage, (safe + 1) * perPage);
}
